/* eslint-disable max-lines */
import { useMatchedFaceLayers } from '@hakimo-ui/hakimo/feature-shared';
import {
  AlarmVideoDetails,
  AlarmVideoMarker,
  RgMatchedFace,
} from '@hakimo-ui/hakimo/types';
import {
  FaceProfileOverlay,
  VideoHighlightBox,
} from '@hakimo-ui/hakimo/ui-elements';
import { onTimestampClick } from '@hakimo-ui/hakimo/util';
import { Alert, useRerenderOnWindowResize } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import AlarmVideoFooter from './AlarmVideoFooter';
import { useFaceDetectionSeek } from './useFaceDetectionSeek';
import { getTimeStr } from './util';

interface Props {
  videoPath: string;
  videoDetails?: AlarmVideoDetails;
  onErrorLoadingVideo: () => void;
  matchedFaces?: RgMatchedFace[];
  cameraName?: string;
  alarmType?: string;
  disableDownload?: boolean;
}

const EVENT_INDICATOR_OFFSET = 1;

// videoTimeSec is the detection frame's own presentation timestamp, taken
// verbatim from the container (ffprobe packet=pts_time). Seeking to it lands
// exactly on the frame boundary, which resolves to the frame *before* the
// detection, so the face box ends up a frame ahead of the face. Nudge into the
// frame's own interval: comfortably past the timestamp's printed precision,
// and still well inside one frame at any rate these videos use.
const FRAME_NUDGE_SEC = 0.01;

export function AlarmVideo(props: Props) {
  const {
    videoPath,
    videoDetails,
    onErrorLoadingVideo,
    matchedFaces,
    cameraName,
    alarmType,
    disableDownload,
  } = props;
  const [hasError, setHasError] = useState(false);
  const [activeMarker, setActiveMarker] = useState<AlarmVideoMarker>();
  const [isFaceDetectionActive, setIsFaceDetectionActive] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<HTMLSpanElement>(null);
  const markers = videoDetails?.markers;
  const videoStartTime = videoDetails?.startTime ?? undefined;

  useRerenderOnWindowResize();

  const { overlayFaces, boxes: faceBoxes } = useMatchedFaceLayers({
    matchedFaces,
    cameraName,
    videoStartTime,
    alarmType,
  });

  const onErrorSource = () => {
    setHasError(true);
    onErrorLoadingVideo();
  };

  const onTimestampClickCb = (timestamp: number) => {
    const videoElem = videoRef.current;

    if (!videoElem) {
      return;
    }

    onTimestampClick(videoElem, timestamp);
  };

  const onFaceSeek = (timestamp: number) => {
    const videoElem = videoRef.current;

    if (!videoElem) {
      return;
    }

    videoElem.currentTime = Math.max(timestamp, 0) + FRAME_NUDGE_SEC;
    videoElem.pause();
  };

  const {
    facesWithTime,
    label: faceDetectionLabel,
    onClick: onFaceDetectionClick,
  } = useFaceDetectionSeek(matchedFaces, onFaceSeek);

  // One-shot load lifecycle: only the video element's identity (a new
  // videoPath remounts it, see `key={videoPath}` below) should reset this.
  useEffect(() => {
    const videoElem = videoRef.current;

    if (!videoElem) {
      return;
    }

    const onLoadedMetadata = () => setIsVideoLoaded(true);

    videoElem.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      setIsVideoLoaded(false);
      videoElem.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [videoPath]);

  // Recurring playback tracking, keyed on the data it reads. Runs on the
  // already-mounted element, so it must not touch `isVideoLoaded`.
  useEffect(() => {
    const videoElem = videoRef.current;

    if (!videoElem) {
      return;
    }

    const onTimeUpdate = () => {
      const timerElem = timerRef.current;

      if (timerElem && typeof videoDetails?.startTime === 'string') {
        timerElem.textContent = getTimeStr(
          videoDetails.startTime,
          videoElem.currentTime
        );
      }

      setActiveMarker(
        markers?.find(
          (marker) =>
            Math.abs(videoElem.currentTime - marker.timestamp) <
            EVENT_INDICATOR_OFFSET
        )
      );

      setIsFaceDetectionActive(
        facesWithTime.some(
          (face) =>
            Math.abs(videoElem.currentTime - face.videoTimeSec) <
            EVENT_INDICATOR_OFFSET
        )
      );
    };

    videoElem.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      videoElem.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [markers, videoDetails, facesWithTime]);

  const timer = (
    <span
      ref={timerRef}
      className="dark:text-dark-text mr-4 text-sm font-medium text-gray-900"
    />
  );

  return (
    <>
      {hasError ? (
        <div className="p-6">
          <Alert>Cannot play video</Alert>
        </div>
      ) : (
        <div className="relative">
          <video
            ref={videoRef}
            key={videoPath}
            controls
            controlsList={disableDownload ? 'nodownload' : undefined}
            onContextMenu={
              disableDownload ? (e) => e.preventDefault() : undefined
            }
            className="dark:bg-dark-bg/70 before:border-hakimo-yellow max-h-[48rem] w-full bg-gray-200"
          >
            <source src={videoPath} type="video/mp4" onError={onErrorSource} />
            <Alert>Your browser cannot play the provided video file.</Alert>
          </video>
          {overlayFaces.length > 0 && (
            <FaceProfileOverlay matchedFaces={overlayFaces} />
          )}
          {isVideoLoaded && (
            <VideoHighlightBox
              boxes={faceBoxes}
              videoRef={videoRef}
              videoHeight={videoRef.current?.videoHeight}
              videoWidth={videoRef.current?.videoWidth}
              videoRect={videoRef.current?.getBoundingClientRect()}
            />
          )}
          {activeMarker && (
            <div
              className={clsx(
                'pointer-events-none absolute inset-1 z-10 border-4',
                activeMarker?.type === 'Warning' && 'border-hakimo-yellow',
                activeMarker?.type === 'Info' &&
                  'border-primary-800 dark:border-primary-200',
                activeMarker?.type === 'Error' &&
                  'border-red-800 dark:border-red-200'
              )}
            />
          )}
        </div>
      )}
      <AlarmVideoFooter
        markers={markers}
        activeMarker={activeMarker}
        onTimestampClick={onTimestampClickCb}
        timer={timer}
        faceDetection={
          faceDetectionLabel
            ? {
                label: faceDetectionLabel,
                isActive: isFaceDetectionActive,
                onClick: onFaceDetectionClick,
              }
            : undefined
        }
      ></AlarmVideoFooter>
    </>
  );
}

export default AlarmVideo;
